/**
 * The resolver: base weapon + fitted parts -> the numbers the game actually uses.
 *
 * This is the seam the rest of the weapon code was built around. `WeaponSystem` does
 * not know that attachments exist; it asks its inventory slot for `def` and gets an
 * *effective* weapon definition — same shape, same field names, same units as the
 * base table. `Ballistics`, `ViewModel`, the HUD and the crosshair all read that one
 * object. There is no modifier logic anywhere in the fire path, and there is exactly
 * one place in the codebase where an attachment's effect on a number is computed.
 *
 * ## The order
 *
 *     base -> additive flat -> additive percent -> multiplicative percent
 *          -> clamp -> derived
 *
 * `schema.js` implements the middle four. This file owns the two ends: it builds the
 * base copy, and it runs the derive stage — which is where the properties that must
 * survive any build are re-established rather than hoped for.
 *
 * ## What the derive stage guarantees
 *
 * Four things are *derived*, every resolve, from whatever the modifiers produced.
 * None of them can be broken by any combination of attachments, present or future,
 * because none of them is stored:
 *
 *  1. **`spread.ads` is exactly 0.** Assigned, not clamped. No path reaches it.
 *  2. **Bloom is a net.** `perShot = bloomDecay * (60/rpm) + bloomGain`, recomputed
 *     against the *resolved* rate of fire. A part that changes the fire rate cannot
 *     pin the cone at zero, and a part that changes the decay cannot either, because
 *     the relationship is re-established after both have been applied.
 *  3. **Recoil magnitude comes out of the climb budget.** `recoil.pitch` is the
 *     resolved `climb30Deg` divided by the weapon's own pattern sum. The pattern
 *     array is passed through by reference and never copied, so it is not merely
 *     unchanged — it is the same object.
 *  4. **A shotgun's one-shot band cannot be extended.** `CLASS_CLAMP.sg` pins
 *     buckshot damage and range at their base values; the pin is lifted only for a
 *     single projectile, which is a slug, which never one-shots anything.
 *
 * ## Caching
 *
 * Resolving is cheap but not free, and doing it per shot would mean the numbers a
 * player is fighting could in principle differ between two shots of one magazine.
 * A build resolves once, into a stable object that is *mutated in place* on the next
 * resolve rather than replaced — `ViewModel` holds a reference to it across frames,
 * and swapping the object underneath it would silently strand the viewmodel on the
 * previous build's ADS timings.
 */

import {
  SLOT_INDEX, CLAMP, classClampFor,
  getPath, setPath, foldPath, accumulate,
} from './schema.js';
import { WEAPONS, climbSum, BASE_OPTIC_ZOOM } from '../weapons.data.js';

const DEG = Math.PI / 180;

export const TUNING = {
  /** Floor on the ADS field-of-view scale, so a 7x cannot produce a keyhole. */
  minFovScale: 0.055,
  /**
   * How hard aim sensitivity follows magnification.
   *
   * 1.0 would keep the on-screen angular rate constant, which reads as unusably
   * slow at high zoom because the player's hand does not know about magnification.
   * 0.75 is the compromise the base table already implies: the sniper's 4x irons sit
   * at fovScale 0.175 and sensScale 0.24, and 0.175^0.75 is 0.27 — near enough that
   * the same exponent reproduces the hand-tuned values it has to live beside.
   */
  sensExponent: 0.75,
  minSensScale: 0.06,

  /**
   * The learnability floor: the weakest a pattern's signal is allowed to be against
   * its own noise, at shot 30.
   *
   * The roster was tuned to sit between 34.7:1 (Akita) and 70.1:1 (MXR-17), and a
   * pattern a player can learn to pull against is the single most expensive property
   * in the weapon table. Several attachments legitimately trade determinism away —
   * a stripped receiver, a low comb, a single-fire sear — and three or four of them
   * on a shotgun would otherwise take it below anything the roster ever measured.
   *
   * So the floor is enforced on the property itself rather than on a proxy. The
   * ratio depends only on the pattern table and on `recoil.random` (the magnitude
   * cancels), so it inverts cleanly into a ceiling on `random`, applied in
   * `deriveEffective`. 34.5 sits just under the roster's own minimum, so no stock
   * weapon's noise is touched by it — only builds that were about to go below the
   * worst number this game has ever shipped.
   */
  minPatternSnr: 34.5,
};

/**
 * `sum(P)` and `sqrt(sum(P^2)/3)` over the first 30 rounds of a pattern — the two
 * halves of `patternQuality`, cached per pattern table because they are pure
 * functions of an array that never changes.
 */
const _snrTerms = new WeakMap();
function snrTerms(pattern) {
  let t = _snrTerms.get(pattern);
  if (t) return t;
  const m = Math.min(30, pattern.length);
  let sum = 0;
  let sumSq = 0;
  for (let i = 0; i < m; i++) {
    sum += pattern[i][0];
    sumSq += pattern[i][0] * pattern[i][0];
  }
  t = { sum, rms: Math.sqrt(sumSq / 3) };
  _snrTerms.set(pattern, t);
  return t;
}

/* -------------------------------------------------------------------------- */
/* the base copy                                                               */
/* -------------------------------------------------------------------------- */

/** Sub-objects that must be copied rather than shared with the base table. */
const CLONED = [
  'ttk', 'reload', 'ads', 'spread', 'recoil', 'ergonomics', 'viewKick',
  'shell', 'audio', 'hitboxScale', 'aimSway', 'flinch', 'signature',
  'mobility', 'optic',
];

/**
 * Copy the assembled weapon into `out`, ready to be modified.
 *
 * `pattern` and `damage` are shared by reference on purpose. The pattern is never
 * modified by anything — that is the point of it — and the damage curve is rebuilt
 * from scratch by the derive stage into a fresh array, so the base one is only ever
 * read.
 */
function baseInto(out, def) {
  for (const k of Object.keys(out)) delete out[k];
  Object.assign(out, def);
  for (const k of CLONED) {
    if (def[k] && typeof def[k] === 'object') out[k] = { ...def[k] };
  }
  // Overheat and launcher are null on a stock weapon and are replaced wholesale by a
  // `set` when they are not, so there is nothing to clone.
  out.overheat = def.overheat ?? null;
  out.launcher = def.launcher ?? null;
  out.laser = null;

  /* ------------------------------- virtual ------------------------------- */
  // These three do not exist on the base weapon. They are how an attachment talks
  // about a curve or about a whole family of timings without naming each entry.
  out.damage = { mult: 1, range: 1 };
  out.reload.scale = 1;

  // Kept so the resolved def can say what it was resolved from, and so a second
  // resolve of the same object starts from the base rather than from itself.
  out.base = def;
  out.attachments = [];
  out.pointsUsed = 0;
  return out;
}

/* -------------------------------------------------------------------------- */
/* resolve                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Resolve a weapon and its fitted parts into `out`.
 *
 * @param weaponId  id in `WEAPONS`
 * @param atts      array of attachment entries (already validated as compatible)
 * @param out       a stable object to write into; created if omitted
 * @returns `out`
 */
export function resolveInto(weaponId, atts, out = {}) {
  const def = WEAPONS[weaponId];
  if (!def) throw new Error(`[attachments] no weapon "${weaponId}"`);
  baseInto(out, def);

  const list = (atts ?? []).filter(Boolean);
  out.attachments = list.map((a) => a.id);
  out.pointsUsed = list.reduce((n, a) => n + (a.cost ?? 1), 0);

  if (!list.length) {
    deriveEffective(out, def);
    return out;
  }

  /* ------------------------- accumulate, then fold ------------------------ */
  // Accumulation is order-independent by construction for `add`, `mult` and `scale`
  // (sum, sum, product). Only `set` breaks ties, and it breaks them on slot rank —
  // never on the order the player happened to equip things in.
  const bags = new Map();
  for (const a of list) {
    const rank = SLOT_INDEX[a.slot] ?? 0;
    for (const m of a.mods) accumulate(bags, m, rank);
  }

  /**
   * Pellet count is folded before anything else.
   *
   * The shotgun damage and range rails are not the same for a shell of shot and for
   * a slug, and "which of the two is this build?" is answered by `pellets`. Folding
   * it first is what lets `classClampFor` be a lookup rather than a guess.
   */
  if (bags.has('pellets')) {
    setPath(out, 'pellets', foldPath(out.pellets ?? 1, bags.get('pellets'), CLAMP.pellets));
    bags.delete('pellets');
  }

  const clsClamp = classClampFor(def.classId, out.pellets ?? 1);
  for (const [path, bag] of bags) {
    const base = getPath(out, path);
    const clamp = (clsClamp && clsClamp[path]) ?? CLAMP[path];
    setPath(out, path, foldPath(base, bag, clamp));
  }

  /* ------------------------------- the sight ------------------------------ */
  // Merged rather than folded: an optic's zoom, reticle and sensor modes are not
  // quantities that stack, and only one optic can be fitted.
  const optic = list.find((a) => a.optic);
  if (optic) {
    Object.assign(out.optic, optic.optic, { id: optic.id, name: optic.name });
  }

  /**
   * The fitted laser, if any.
   *
   * Recorded as a block rather than as a boolean modifier because the beam is a
   * *thing in the world* — the behaviour systems module draws it — and because an
   * infrared laser is fitted, working, and invisible, which no single flag says.
   */
  const laser = list.find((a) => a.slot === 'laser');
  out.laser = laser ? { id: laser.id, name: laser.name, visible: laser.laserVisible !== false } : null;

  deriveEffective(out, def);
  return out;
}

/* -------------------------------------------------------------------------- */
/* derive                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Re-establish everything that is a consequence of the resolved numbers.
 *
 * Runs on every resolve including the empty one, so a weapon with nothing fitted
 * goes down exactly the same path as a weapon with five parts on it. That is
 * deliberate: a bug that only appears once something is attached is a bug nobody
 * finds, and a stock weapon that resolves to something other than its own base table
 * is a bug this function is the only place to catch.
 */
export function deriveEffective(out, def) {
  const cls = def.classId;

  /* ------------------------------ accuracy -------------------------------- */
  // Assigned, never clamped, never modified. This is the one number in the weapon
  // system that is allowed no nuance at all: fully shouldered and stationary is a
  // cone of exactly zero, for all twenty-three weapons, whatever is bolted to them.
  out.spread.ads = 0;

  // Bloom as a net. Recomputed against the *resolved* rate of fire, so a rapid-fire
  // kit and a spring kit both leave the relationship intact rather than each having
  // to remember to maintain it.
  const s = out.spread;
  s.perShot = s.maxBloom > 0 ? s.bloomDecay * (60 / out.rpm) + s.bloomGain : 0;

  /* -------------------------------- recoil -------------------------------- */
  // Magnitude out of the budget, shape out of the table, and the table is the same
  // array object the base weapon carries.
  out.pattern = def.pattern;
  const pitch = (out.recoil.climb30Deg * DEG) / climbSum(out.pattern);
  out.recoil.pitch = pitch;
  out.recoil.yaw = pitch * out.recoil.yawRatio;

  // The learnability floor. `snr = sum / (random * rms)`, so the ceiling on noise
  // falls straight out of it. See `TUNING.minPatternSnr`.
  const { sum, rms } = snrTerms(out.pattern);
  const maxRandom = sum / (TUNING.minPatternSnr * rms);
  if (out.recoil.random > maxRandom) out.recoil.random = maxRandom;
  out.patternSnr = sum / (out.recoil.random * rms);

  /* -------------------------------- damage -------------------------------- */
  /**
   * Damage is authored per projectile, so changing the projectile count would
   * otherwise change the shell's total output as a side effect.
   *
   * A slug that turns eight pellets into one would do an eighth of the damage of the
   * shell it replaced, which is not what "one solid projectile instead of shot"
   * means in any language. So the per-projectile figure is scaled by
   * `basePellets / pellets` first, conserving the trigger pull's total output, and
   * `damage.mult` then expresses the change the attachment actually intends.
   */
  const basePellets = def.pellets ?? 1;
  const pellets = out.pellets ?? 1;
  const pelletConserve = basePellets / Math.max(1, pellets);

  /**
   * The shotgun ceiling.
   *
   * `classClampFor` pinned both of these at 1.0 during the fold for a build that is
   * still throwing shot, which is what stops any buckshot build from moving the
   * distance at which a shell kills in one. `pelletConserve` is deliberately applied
   * *after* that pin and is exempt from it: turning eight pellets into one and
   * multiplying the survivor's damage by eight conserves the shell's output rather
   * than increasing it, and the attachment's own `damage.mult` — still pinned at or
   * below 1.0 — is what decides where a slug actually lands.
   */
  const dmgMult = out.damage.mult * pelletConserve;
  const rangeMult = out.damage.range;

  out.damage = [];
  for (const [dist, dmg] of def.damage) out.damage.push([dist * rangeMult, dmg * dmgMult]);
  // Kept alongside the curve so the gunsmith screen can show what moved without
  // having to diff two arrays.
  out.damageMult = dmgMult;
  out.rangeMult = rangeMult;

  /* -------------------------------- reload -------------------------------- */
  const rs = out.reload.scale;
  if (rs !== 1) {
    for (const k of Object.keys(def.reload)) out.reload[k] = def.reload[k] * rs;
  }
  out.reload.scale = rs;

  /* --------------------------------- sight -------------------------------- */
  /**
   * Magnification into field of view and sensitivity.
   *
   * `ads.fovScale` and `ads.sensScale` are hand-tuned per weapon and are not
   * overwritten — they are *scaled* by how much more magnification the fitted optic
   * offers than the weapon's own irons. A rifle at 1x irons with a 3x fitted ends up
   * at a third of its own tuned FOV; a sniper whose irons are already worth 4x gets
   * proportionally less out of the same glass, which is correct and is why the base
   * zoom is per class rather than a global 1.
   */
  const baseZoom = BASE_OPTIC_ZOOM[cls] ?? 1;
  out.optic.baseZoom = baseZoom;
  const zoom = out.optic.zoom || baseZoom;
  if (zoom !== baseZoom) {
    const r = baseZoom / zoom;
    out.ads.fovScale = Math.max(TUNING.minFovScale, def.ads.fovScale * r);
    out.ads.sensScale = Math.max(TUNING.minSensScale, def.ads.sensScale * Math.pow(r, TUNING.sensExponent));
  }

  /* ---------------------------- fire control ------------------------------ */
  // A converted weapon needs the fields its new mode reads. Filled here rather than
  // by the attachment, so a conversion cannot forget one and leave `burstCount`
  // undefined on a weapon that is now a burst weapon.
  if (out.fireMode === 'burst') {
    out.burstCount = Math.max(2, Math.round(out.burstCount ?? def.burstCount ?? 3));
    out.burstDelay = out.burstDelay ?? def.burstDelay ?? 0.2;
  }
  if (out.fireMode === 'pump') out.pumpTime = out.pumpTime ?? def.pumpTime ?? 0.62;
  if (out.fireMode === 'bolt') out.boltCycle = out.boltCycle ?? def.boltCycle ?? 1.0;

  /* -------------------------------- ammo ---------------------------------- */
  out.magSize = Math.max(1, Math.round(out.magSize));
  // Reserve is a property of what the player is carrying, not of the magazine, so an
  // extended magazine does not conjure spare rounds — it only changes how many of
  // them are in the weapon at once. The ceiling is raised where it would otherwise
  // sit below one magazine, which would make the weapon unreloadable.
  out.maxReserve = Math.max(def.maxReserve, out.magSize);
  out.startReserve = Math.min(def.startReserve, out.maxReserve);

  return out;
}

/* -------------------------------------------------------------------------- */
/* the cache                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * One resolved object per weapon, reused for the life of the process.
 *
 * Keyed by weapon rather than by build, because a weapon has exactly one build at a
 * time and the identity of the object matters more than the hit rate: `ViewModel`
 * and the HUD hold references to it between frames.
 */
export class ResolveCache {
  constructor() {
    this._byWeapon = new Map();
    this._signature = new Map();
    this.resolves = 0;
    this.hits = 0;
  }

  /**
   * The effective definition for a weapon and a list of attachment entries.
   *
   * Re-resolves only when the build actually changed. The signature is the ordered
   * attachment ids — order-insensitive results, order-sensitive cache key, which is
   * the safe way round: an equal build that was equipped in a different order costs
   * one extra resolve, and an unequal build can never be mistaken for a hit.
   */
  get(weaponId, atts) {
    const sig = (atts ?? []).map((a) => a.id).join('|');
    let out = this._byWeapon.get(weaponId);
    if (out && this._signature.get(weaponId) === sig) {
      this.hits++;
      return out;
    }
    if (!out) {
      out = {};
      this._byWeapon.set(weaponId, out);
    }
    resolveInto(weaponId, atts, out);
    this._signature.set(weaponId, sig);
    this.resolves++;
    return out;
  }

  /** Force the next `get` for this weapon (or every weapon) to re-resolve. */
  invalidate(weaponId) {
    if (weaponId == null) this._signature.clear();
    else this._signature.delete(weaponId);
  }

  get stats() {
    return { resolves: this.resolves, hits: this.hits, cached: this._byWeapon.size };
  }
}
